import React, { useState } from 'react';

const faqs = [
  {
    q: 'Does FarmLog work for goat farming?',
    a: 'Yes. FarmLog supports both fish and goat operations in a single platform. Track breeding, kidding, health, vaccinations, feed, weight, and sales for your goat herd alongside your fish ponds.',
  },
  {
    q: 'What if I only have fish ponds or only goats?',
    a: 'That works perfectly. You can use just the fish features or just the goat features. The web app shows you only the relevant sections based on your farm setup.',
  },
  {
    q: 'Do I need internet to use FarmLog?',
    a: 'You can enter data even without a strong internet connection. When you are back online, everything syncs automatically. Designed for rural areas where connectivity is unreliable.',
  },
  {
    q: 'How is FarmLog different from NavFarm or KisanAqua?',
    a: 'NavFarm is a complex ERP for large operations starting at much higher prices. KisanAqua focuses on marketplace and shrimp trading. FarmLog is specifically built for small and medium Indian farmers who need simple record-keeping for fish and goat — at Rs500/month.',
  },
  {
    q: 'Can my farm workers also use the platform?',
    a: 'Yes. FarmLog supports multiple users per farm with role-based access. Admins manage everything, managers can add data and view reports, and workers can enter daily logs.',
  },
  {
    q: 'What reports can I see?',
    a: 'Financial reports (income, expense, profit/loss), feed trends, medicine tracking, mortality reports, inventory status, revenue projections, cost analysis, and cycle-by-cycle comparisons. All from your phone.',
  },
  {
    q: 'Is my data safe?',
    a: 'Your data is stored securely on cloud servers (MongoDB Atlas). Only you and your authorized team can access your farm data. We do not share or sell your information.',
  },
  {
    q: 'Can I try before I pay?',
    a: 'Yes. Visit demo.farmlog.in to see the platform with sample farm data — no signup needed. You can explore all features before deciding.',
  },
  {
    q: 'How do I get support?',
    a: 'WhatsApp support only — fast and convenient. Send a message, we reply quickly so you can get back to the farm. No complicated ticket systems.',
  },
];

const FAQItem: React.FC<{ faq: typeof faqs[0]; isOpen: boolean; toggle: () => void }> = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-gray-200 last:border-0">
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between py-5 text-left gap-4"
    >
      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{faq.q}</h3>
      <svg
        className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <p className="pb-5 text-gray-600 text-sm leading-relaxed pr-8">{faq.a}</p>
    )}
  </div>
);

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-gray-600 text-lg">
            Everything you need to know about FarmLog.
          </p>
        </div>
        <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-100 px-6">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              isOpen={openIndex === i}
              toggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
